import GMComponent from './gmcomponent';
import { default as CustomizableRadialMenu } from './radial-menu/RadialMenu.min';

const COLOR_CHANGE_EVENT_TYPE = 'color.update';
const OUTER_CIRCLE = 100;
const INNER_CRICLE = 50;

/**
 * @typedef {Object} ItemOption
 * @property {string} icon The button icon.
 * @property {function():Array<import('../radial-menu/RadialMenu.min').RadialMenuButton>} [next] Give the next radial menu state after button click. If undefined, menu is closed.
 */

/**
 * @typedef {import('../radial-menu/RadialMenu.min').RadialMenuButton|ItemOption} ItemOptions
 */

/**
 * The component which display loading.
 */
export default class RadialMenu extends GMComponent {
  /**
     * @constructor
     */
  constructor() {
    super();

    this.rm_ = new CustomizableRadialMenu({
      innerCircle: INNER_CRICLE,
      outerCircle: OUTER_CIRCLE,
      fontFamily: 'fontello, Open Sans',
      backgroundColor: '#00a5d3',
      hoverBackgroundColor: '#afcb38',
      buttonGap: Math.PI / 60,
      textColor: 'white',
      borderColor: 'transparent', 
      rotation: Math.PI / 2, 
      fontSize: 18, 
      shadowOffsetX: 0,
      shadowOffsetY: 1,
      shadowBlur: 4,
      shadowColor: 'rgba(0,0,0,0.6)'
    });

    this.dom = this.rm_.canvas;
    this.dom.style['user-select'] = 'none';

    /** 
         * Map from css icon class name to unicode content.
         * @type {Object<string,string}
         */
    this.icons_ = {};

    document.addEventListener(COLOR_CHANGE_EVENT_TYPE, () => {
      setTimeout(() => {
        const rootStyle = getComputedStyle(document.documentElement);
        this.rm_.backgroundColor = rootStyle.getPropertyValue('--blue-color');
        this.rm_.hoverBackgroundColor = rootStyle.getPropertyValue('--green-color');
        this.rm_.textColor = rootStyle.getPropertyValue('--white-color');
        this.rm_.draw();
      }, 0);
    })
  }

  /**
     * Show the element.
     * @param {Array<ItemOptions>} items The menu items. 
     */
  async show(items) {
    if (items instanceof Promise) {
      items = await items;
    }
    if(items) {
      for(let btn of items) {
        if(btn.icon) {
          btn.text = this.replaceCSSClass_(`${btn.icon}`);
        }
        const oldAction = btn.action;
        btn.action = event => {
          oldAction(event);
          this.show(btn.next ? btn.next() : null);
        };
      }
      this.rm_.addButtons(items);
      this.rm_.show();
    } else {
      this.hide();
    }
  }

  /**
     * Hide the element.
     */
  hide() {
    this.rm_.hide();
  }

  /**
     * Set the center of the menu.
     * @param {Array<number>} cneter The center coordinate in pixel.
     */
  setCenter(center) {
    this.rm_.setPos(center[0] - OUTER_CIRCLE, center[1] - OUTER_CIRCLE);
  }

  /**
     * Replace Icon CSS class name by its uncode content.
     * @param {string} iconText The icon css class name in the icon button field.
     * @return {string} The icon button field with icon css class name replaced with unicode content.
     */
  replaceCSSClass_(iconText = '') {
    return iconText.split(' ').map(word => {
      if(word.startsWith('.icon-')) {
        word =  this.getCSSClassIconContent_(word);
      }
      return word;
    }).join(' ');
  }

  /**
     * Get the unicode content of the css class icon.
     * @param {string} cssClassName The CSS class icon name.
     * @return {string} The unicode of the css before content.
     */
  getCSSClassIconContent_(cssClassName) {
    if(!this.icons_[cssClassName]) {
      const listStyleSheets = document.styleSheets;
      for (const styleSheets of listStyleSheets) {
        const filters = Object.values(styleSheets.cssRules).filter(rule => rule.selectorText === `${cssClassName}::before`);
        if (filters.length) {
          this.icons_[cssClassName] = filters[0].style.content.replace(/"/g,'');
          break;
        }
      }      
    }
    return this.icons_[cssClassName];
  }
}